// ── Walk-readiness marker (`data-tcp-ready`) ────────────────────────────────
// Contract owner: vjal-test-sidecar `src/harvest.ts` (`waitForTcpReadyIfPresent`).
//
//   1. `index.html` publishes `<body data-tcp-ready="false">` from the first byte.
//   2. A surface flips it to "true" ONLY when it is TERMINAL — every settled
//      posture, including the honest hard-error / denial surfaces. It is never
//      flipped while auth, record-scope resolution, or a first data read is in
//      flight, otherwise the walk harvests the loading shell and honest content
//      reads as MISSING (the CRM run-29 race class).
//   3. It resets to "false" on unmount so a later navigation re-arms the wait.
//
// Because the marker is published from the first byte it is ALWAYS present, so
// every reachable surface must eventually flip it — a surface that never does
// turns the walk into `probe_failed` instead of a harvest. `CoachingRouteScreen`
// is therefore the default owner (it flips as soon as the shell settles) and
// delegates only to the screens listed in `WALK_READINESS_DELEGATING_SCREEN_IDS`,
// whose own first read is still in flight when the shell settles.

import { createContext, createElement, useContext, useEffect, useLayoutEffect, useState, useSyncExternalStore } from "react";
import type { ReactElement, ReactNode } from "react";

type WalkReadinessListener = () => void;

export interface WalkReadinessRegistry {
  addHold: () => () => void;
  getSnapshot: () => number;
  subscribe: (listener: WalkReadinessListener) => () => void;
}

function createWalkReadinessRegistry(): WalkReadinessRegistry {
  const holds = new Set<symbol>();
  const listeners = new Set<WalkReadinessListener>();
  let activeHoldCount = 0;

  const notify = () => {
    for (const listener of listeners) listener();
  };

  return {
    addHold() {
      const token = Symbol("walk-readiness-hold");
      holds.add(token);
      activeHoldCount = holds.size;
      notify();

      let released = false;
      return () => {
        if (released) return;
        released = true;
        if (!holds.delete(token)) return;
        activeHoldCount = holds.size;
        notify();
      };
    },
    getSnapshot: () => activeHoldCount,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

const defaultWalkReadinessRegistry = createWalkReadinessRegistry();
const WalkReadinessContext = createContext<WalkReadinessRegistry>(defaultWalkReadinessRegistry);

export function WalkReadinessProvider({ children }: { children: ReactNode }): ReactElement {
  const [registry] = useState(createWalkReadinessRegistry);
  return createElement(WalkReadinessContext.Provider, { value: registry }, children);
}

export function useWalkReadinessRegistry(): WalkReadinessRegistry {
  return useContext(WalkReadinessContext);
}

export function useWalkReadinessHoldCount(): number {
  const registry = useWalkReadinessRegistry();
  return useSyncExternalStore(registry.subscribe, registry.getSnapshot, registry.getSnapshot);
}

/** Keep a readiness hold active for a query's initial pending fetch. */
export function useWalkReadinessHold(active: boolean): void {
  const registry = useWalkReadinessRegistry();
  useLayoutEffect(() => {
    if (!active) return;
    return registry.addHold();
  }, [active, registry]);
}

/**
 * Screens that own their own flip because the shell settling does not mean
 * their content has: `CoachingRouteScreen` stays silent for these and the
 * screen body flips on its own first settle (trainer two-phase precedent).
 *
 * A screen listed here MUST call {@link useWalkReadyMarker} on every posture it
 * can render (data, empty, error, denial) or its walks fail as `probe_failed`.
 */
export const WALK_READINESS_DELEGATING_SCREEN_IDS: Record<string, true> = { dashboard: true, "org-admin-home": true };

/**
 * Publish the walk-readiness marker for the surface that owns it.
 *
 * A settled shell remains false for one macrotask before publishing true.
 * Query holds register in layout effects during the same commit, and the
 * delayed re-check catches dependent queries enabled by settled data before
 * the sidecar can harvest a loading shell.
 */
export function useWalkReadyMarker(ready: boolean): void {
  const registry = useWalkReadinessRegistry();
  const holdCount = useWalkReadinessHoldCount();

  useEffect(() => {
    // A delegating parent intentionally remains silent: its child owns the
    // marker. Cleanup still re-arms the false marker on route/unmount changes.
    if (!ready) return;

    document.body.dataset.tcpReady = "false";
    const settleTimer = setTimeout(() => {
      if (registry.getSnapshot() === 0) document.body.dataset.tcpReady = "true";
    }, 0);

    return () => {
      clearTimeout(settleTimer);
      document.body.dataset.tcpReady = "false";
    };
  }, [holdCount, ready, registry]);
}
